import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useTranslation } from "react-i18next";
import { Box, Button, Card, Fade, Stack, Typography, styled, type CardProps } from "~/common/mui";

export type GoToSummaryScrollState = "expanded" | "collapsed";

export interface GoToSummaryViewModel {
  unfilledControlCount: number;
  conflictCount: number;
  scrollState: GoToSummaryScrollState;
}

export interface GoToSummaryProps extends Omit<CardProps, "title"> {
  viewModel: GoToSummaryViewModel;
  onGoToSummary: () => void;
  disabled?: boolean;
}

export interface GoToSummaryActionProps {
  viewModel: GoToSummaryViewModel;
  onGoToSummary: () => void;
  disabled?: boolean;
}

type GoToSummaryAppearance = "incomplete" | "conflict" | "ready";

interface GoToSummaryPresentation {
  appearance: GoToSummaryAppearance;
  actionEnabled: boolean;
}

const getPresentation = ({ unfilledControlCount, conflictCount }: GoToSummaryViewModel): GoToSummaryPresentation => {
  if (conflictCount > 0) {
    return { appearance: "conflict", actionEnabled: false };
  }
  if (unfilledControlCount > 0) {
    return { appearance: "incomplete", actionEnabled: false };
  }
  return { appearance: "ready", actionEnabled: true };
};

const SummaryRoot = styled(Card, {
  shouldForwardProp: (prop) => prop !== "appearance",
})<{ appearance: GoToSummaryAppearance }>(({ appearance, theme }) => ({
  width: "60%",
  maxWidth: "100%",
  alignSelf: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  overflow: "hidden",
  border: 0,
  borderRadius: theme.spacing(2),
  boxShadow: "none",
  backgroundColor: appearance === "ready" ? theme.palette.success.container : theme.palette.surface.bright,
}));

const StickySummaryRoot = styled(Box)(({ theme }) => ({
  position: "sticky",
  bottom: theme.spacing(2),
  zIndex: theme.zIndex.appBar,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
}));

const SummaryContent = styled(Stack)(({ theme }) => ({
  flex: "1 1 auto",
  minWidth: 0,
  maxWidth: "100%",
  maxHeight: theme.spacing(9),
  opacity: 1,
  overflow: "hidden",
}));

const TitleRow = styled(Stack)({
  alignItems: "center",
  whiteSpace: "nowrap",
});

const SummaryAction = styled(Button)(({ theme }) => ({
  flexShrink: 0,
  minWidth: theme.spacing(19.125),
  height: theme.spacing(5),
}));

const getMissingSelectionDescription = (count: number) =>
  count === 1
    ? "1 more selection is required to complete this configuration"
    : `${count} selections more are required to complete this configuration`;

const getConflictDescription = (count: number) =>
  count === 1
    ? "1 conflicting selection must be resolved to complete this configuration"
    : `${count} conflicting selections must be resolved to complete this configuration`;

export const GoToSummaryAction = ({ viewModel, onGoToSummary, disabled = false }: GoToSummaryActionProps) => {
  const { t } = useTranslation();
  const { actionEnabled } = getPresentation(viewModel);
  const actionLabel = t("feature.configurator.v2.summary.goTo", "Go to summary");

  return (
    <SummaryAction
      color="primary"
      variant="contained"
      disabled={disabled || !actionEnabled}
      onClick={onGoToSummary}
      aria-label={actionLabel}
    >
      {actionLabel}
    </SummaryAction>
  );
};

export const GoToSummary = ({ viewModel, onGoToSummary, disabled = false, ...cardProps }: GoToSummaryProps) => {
  const { t } = useTranslation();
  const presentation = getPresentation(viewModel);
  const collapsed = viewModel.scrollState === "collapsed";
  const title =
    presentation.appearance === "ready"
      ? t("feature.configurator.v2.summary.completed", "Completed")
      : presentation.appearance === "conflict"
      ? t("feature.configurator.v2.summary.conflicts", "Resolve conflicts")
      : t("feature.configurator.v2.summary.missing", "Missing selections");
  const description =
    presentation.appearance === "ready"
      ? t("feature.configurator.v2.summary.completedDescription", "All selections made for this configuration")
      : presentation.appearance === "conflict"
      ? t("feature.configurator.v2.summary.conflictDescription", {
          count: viewModel.conflictCount,
          defaultValue: getConflictDescription(viewModel.conflictCount),
        })
      : t("feature.configurator.v2.summary.missingDescription", {
          count: viewModel.unfilledControlCount,
          defaultValue: getMissingSelectionDescription(viewModel.unfilledControlCount),
        });

  return (
    <>
      <Fade in={!collapsed} timeout={180} mountOnEnter unmountOnExit>
        <SummaryRoot {...cardProps} appearance={presentation.appearance} aria-label={title}>
          <SummaryContent gap={1}>
            <TitleRow direction="row" gap={1}>
              <Typography variant="bodyLarge" sx={{ fontWeight: 700 }}>
                {title}
              </Typography>
              {presentation.appearance === "ready" ? (
                <CheckRoundedIcon color="success" sx={(theme) => ({ fontSize: theme.spacing(2.5) })} />
              ) : (
                <WarningAmberRoundedIcon color="warning" sx={(theme) => ({ fontSize: theme.spacing(2.5) })} />
              )}
            </TitleRow>
            <Typography variant="bodyMedium" sx={{ color: "text.secondary" }}>
              {description}
            </Typography>
          </SummaryContent>
          <GoToSummaryAction viewModel={viewModel} disabled={disabled} onGoToSummary={onGoToSummary} />
        </SummaryRoot>
      </Fade>
      <Fade in={collapsed} timeout={180} mountOnEnter unmountOnExit>
        <StickySummaryRoot {...cardProps} aria-label={title}>
          <GoToSummaryAction viewModel={viewModel} disabled={disabled} onGoToSummary={onGoToSummary} />
        </StickySummaryRoot>
      </Fade>
    </>
  );
};
